/**
 * Cart Drawer Cross-Sell Color Personalization — shared helpers
 * File: assets/cart-cross-sell-color-match.js
 *
 * Purpose
 * -------
 * When a customer adds a product to the cart, the cross-sell cards in the
 * cart drawer (rendered by snippets/cart-upsell-list.liquid) should try to
 * show the same color as the item that was just added, falling back to
 * each cross-sell's own default color/variant when it doesn't offer that
 * color.
 *
 * This file is the single shared place that:
 *   1. Decides which product option counts as "color" — by reusing the
 *      theme's existing `settings.color_option_names` setting (the same
 *      setting already used by snippets/product-variant-picker.liquid and
 *      snippets/custom-color-swatch.liquid), exposed to JS as
 *      `window.NOBLColorOptionNames` from layout/theme.liquid. This is
 *      intentionally NOT a second/competing color convention.
 *   2. Extracts the color of a cart line item from a Shopify
 *      `/cart/add.js` (or `/cart/change.js`) response, matching by the
 *      option's NAME rather than its position — so this keeps working
 *      even if two products order their options differently.
 *   3. Stores/retrieves the "last added color" across the add → cart
 *      drawer re-render round trip via sessionStorage (survives the
 *      drawer's HTML being fully replaced by the Section Rendering API,
 *      but intentionally does not persist across browser sessions).
 *
 * Consumers:
 *   - assets/product-form.js       -> setLastAddedColor() after a
 *                                      successful add-to-cart.
 *   - assets/cart-upsell-list.js   -> getLastAddedColor() /
 *                                      extractColorFromLineItem() when
 *                                      deciding which swatch to
 *                                      pre-select on each cross-sell card.
 */
(function () {
  'use strict';

  if (window.NOBLCrossSellColorMatch) return;

  var STORAGE_KEY = 'nobl:lastAddedCartColor';

  function getColorOptionNames() {
    return Array.isArray(window.NOBLColorOptionNames) && window.NOBLColorOptionNames.length
      ? window.NOBLColorOptionNames
      : ['Color', 'color', 'Colour', 'colour'];
  }

  // Mirrors the normalization snippets/custom-color-swatch.liquid already
  // applies when comparing color values (downcase | strip | replace '*').
  function normalize(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase().replace(/\*/g, '');
  }

  function isColorOptionName(name) {
    var normalized = normalize(name);
    if (!normalized) return false;
    return getColorOptionNames().some(function (candidate) {
      return normalize(candidate) === normalized;
    });
  }

  // `item` is a Shopify cart line item object as returned by /cart/add.js,
  // /cart/change.js, or /cart.js — all of which include `options_with_values`
  // as an array of { name, value } pairs for that line's variant.
  function extractColorFromLineItem(item) {
    if (!item || !Array.isArray(item.options_with_values)) return '';
    for (var i = 0; i < item.options_with_values.length; i++) {
      var option = item.options_with_values[i];
      if (option && option.value && isColorOptionName(option.name)) {
        return option.value;
      }
    }
    return '';
  }

  function setLastAddedColor(color) {
    try {
      if (color) {
        sessionStorage.setItem(STORAGE_KEY, color);
      } else {
        // No color on the item that was just added (e.g. a single-variant
        // product) - clear any stale value so an earlier add doesn't keep
        // personalizing cross-sells for a product that has no color.
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // sessionStorage unavailable (privacy mode, disabled storage, etc.)
      // Fail silently - cross-sells simply fall back to their defaults.
    }
  }

  function getLastAddedColor() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  window.NOBLCrossSellColorMatch = {
    normalize: normalize,
    isColorOptionName: isColorOptionName,
    extractColorFromLineItem: extractColorFromLineItem,
    setLastAddedColor: setLastAddedColor,
    getLastAddedColor: getLastAddedColor,
  };
})();
